/**
 * DriverActivityPanel — « Mes déplacements » sur l'accueil conducteur web (2026-09-14).
 * =============================================================================
 * Repris de l'onglet Activité de l'APK, élagué : les 12 dernières semaines en
 * barres (le total en gros), puis mes dernières gardes rendues. La traçabilité
 * présentée comme un SERVICE, pas comme un contrôle — le mémo autopartage
 * (§5 P4) la mesure mieux acceptée que prévu, et elle responsabilise.
 *
 * Une lecture serveur (`/drivers/me/activity`, scale=week) + les réservations
 * que l'accueil a déjà (pas d'appel de plus). Rien n'est calculé ici : le
 * serveur rend les buckets, les km et les motifs d'indisponibilité.
 *
 * Sans télématique (`distance.unavailable`), le bloc le dit et montre quand
 * même les gardes : un parc sans GPS reste un parc.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Route, Car } from 'lucide-react';
import { driverSelfAPI } from '../../../api/drivers.js';
import { useFormat } from '../../../i18n/format.js';

/** Des barres en CSS pur — pas de bibliothèque de graphes pour douze nombres. */
function Bars({ series }) {
  const { fmt } = useFormat();
  const max = Math.max(1, ...series.map((b) => b.km || 0));
  return (
    <div className="flex h-24 items-end gap-1" role="img" aria-label="km par semaine" data-testid="activity-bars">
      {series.map((b) => {
        const h = Math.round(((b.km || 0) / max) * 100);
        const label = b.bucket ? b.bucket.slice(5) : '';
        return (
          <div key={b.bucket} className="flex min-w-0 flex-1 flex-col items-center justify-end" title={`${label} · ${fmt.number(Math.round(b.km || 0))} km`}>
            <div className="w-full rounded-t bg-emerald-500" style={{ height: `${Math.max(h, b.km ? 4 : 0)}%` }} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Array} props.reservations - les réservations de l'accueil (toutes, le panneau garde les rendues)
 * @param {Object|null} props.custody - la garde en cours, exclue de « rendues »
 */
export function DriverActivityPanel({ reservations = [], custody = null }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();

  const { data, isLoading } = useQuery({
    queryKey: ['driver-activity', 'week'],
    queryFn: () => driverSelfAPI.getActivity({ scale: 'week' }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const activity = data?.ok ? data : null;
  const series = activity?.distance?.series || [];
  const totalKm = activity?.distance?.total_km;
  const unavailable = activity?.distance?.unavailable || null;

  const returned = useMemo(
    () => (reservations || [])
      .filter((r) => r.status === 'closed' && (!custody || r.id !== custody.reservation?.id))
      .sort((a, b) => new Date(b.planned_end) - new Date(a.planned_end))
      .slice(0, 3),
    [reservations, custody]
  );

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="driver-activity-panel">
      <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
        <Route className="h-4 w-4 text-emerald-600" />
        {t('poolDriver.activity.title')}
      </h2>

      {isLoading ? (
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      ) : activity && !unavailable && totalKm != null ? (
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">{t('poolDriver.activity.last12Weeks')}</p>
          <p className="text-2xl font-bold text-gray-900">{fmt.number(Math.round(totalKm))} km</p>
          {series.length > 0 && <div className="mt-2"><Bars series={series} /></div>}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{t('poolDriver.activity.noTelematics')}</p>
      )}

      <div>
        <p className="mb-1 text-xs uppercase tracking-wide text-gray-500">{t('poolDriver.activity.lastCustodies')}</p>
        {returned.length === 0 ? (
          <p className="text-sm text-gray-500">{t('poolDriver.activity.noCustody')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {returned.map((r) => (
              <li key={r.id} className="flex items-center gap-2 py-1.5 text-sm">
                <Car className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="font-medium text-gray-900">{r.vehicle?.license_plate || r.vehicle_class || '—'}</span>
                <span className="ml-auto text-gray-500">{r.planned_start ? fmt.date(r.planned_start) : '—'}</span>
                {r.destination_label && <span className="hidden truncate text-gray-500 sm:inline">· {r.destination_label}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default DriverActivityPanel;
